import { useCallback, useEffect, useState } from 'react';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import {
  combinarFechaConHoraActual,
  toLocalISOString,
  formatearFechaLocal,
  normalizarFechaISO,
} from '../../../utils/horaLocal';
import {v4 as uuid} from 'uuid';

const ENT_GASTOS = 'gastos';
const ENT_CATEGORIAS = 'categorias-gastos';

export function useGastos() {
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([
        inventarioApi.getAll(ENT_GASTOS),
        inventarioApi.getAll(ENT_CATEGORIAS),
      ]);
      setGastos(g);
      setCategorias(c);
      cache.set(ENT_GASTOS, g);
      cache.set(ENT_CATEGORIAS, c);
    } catch (e) {
      if (isOfflineError(e)) {
        setGastos(cache.get(ENT_GASTOS) || []);
        setCategorias(cache.get(ENT_CATEGORIAS) || []);
      } else console.error('Error fetch gastos', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const agregarCategoria = async (nombre) => {
    try {
      const nueva = await inventarioApi.create(ENT_CATEGORIAS, { id: uuid(), nombre });
      setCategorias(prev => {
        const updated = [...prev, nueva];
        cache.set(ENT_CATEGORIAS, updated);
        return updated;
      });
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: `temp_${Date.now()}`, nombre };
        setCategorias(prev => {
          const updated = [...prev, temp];
          cache.set(ENT_CATEGORIAS, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENT_CATEGORIAS, body: { id: uuid(), nombre } });
      }
    }
  };

  const registrarGasto = async (monto, categoriaId, descripcion = '', metodoPago = 'EFECTIVO', fechaRegistro = null) => {
    const body = {
      id: uuid(),
      monto: parseFloat(monto),
      categoriaId,
      descripcion,
      metodoPago,
      estado: 'ACTIVO',
      fecha: combinarFechaConHoraActual(fechaRegistro),
      motivoAnulacion: null,
    };
    try {
      const nuevo = await inventarioApi.create(ENT_GASTOS, body);
      setGastos(prev => {
        const updated = [...prev, nuevo];
        cache.set(ENT_GASTOS, updated);
        return updated;
      });
      return nuevo;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: `temp_${Date.now()}`, ...body, createdAt: toLocalISOString(), updatedAt: null };
        setGastos(prev => {
          const updated = [...prev, temp];
          cache.set(ENT_GASTOS, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENT_GASTOS, body });
        return temp;
      }
      throw e;
    }
  };

  const editarGasto = async (id, monto, categoriaId, descripcion, metodoPago = 'EFECTIVO') => {
    try { await inventarioApi.patch(ENT_GASTOS, { id, monto: Number(monto), categoriaId, descripcion, metodoPago }); await fetchData(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };
  const anularGasto = async id => {
    if (!window.confirm('¿Anular este gasto?')) return false;
    try { await inventarioApi.patch(ENT_GASTOS, { id, estado: 'ANULADO' }); await fetchData(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };
  const editarFechaGasto = async (id, fecha) => {
    try { await inventarioApi.patch(ENT_GASTOS, { id, fecha }); await fetchData(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };

  const marcarGastoConvertidoALimpiezaLocal = (gastoId, { motivo, limpiezaLocalId } = {}) => {
    setGastos((prev) => {
      const list = prev.map((g) =>
        g.id === gastoId
          ? {
              ...g,
              estado: 'ANULADO',
              motivoAnulacion: motivo
                ? `${motivo} — Conversión a limpieza (pendiente sync)`
                : 'Conversión a limpieza (pendiente sync)',
              convertidoEnLimpiezaId: limpiezaLocalId || g.convertidoEnLimpiezaId,
              pendingSync: true,
              updatedAt: toLocalISOString(),
            }
          : g
      );
      cache.set(ENT_GASTOS, list);
      return list;
    });
  };

  const obtenerGastosActivos = () => gastos.filter(g => g.estado === 'ACTIVO');

  const obtenerGastosDiarios = () => {
    const mapa = {};
    gastos.filter(g => g.estado === 'ACTIVO').forEach(g => {
      const fecha = formatearFechaLocal(normalizarFechaISO(g.fecha));
      if (!mapa[fecha]) mapa[fecha] = { fecha, total: 0, detalles: [] };
      mapa[fecha].total += g.monto;
      mapa[fecha].detalles.push(g);
    });
    return Object.values(mapa);
  };

  return {
    gastos,
    categorias,
    registrarGasto,
    editarGasto,
    anularGasto,
    marcarGastoConvertidoALimpiezaLocal,
    editarFechaGasto,
    agregarCategoria,
    obtenerGastosActivos,
    obtenerGastosPorFecha: fecha => gastos.filter(g => g.estado !== 'ANULADO' && normalizarFechaISO(g.fecha) === normalizarFechaISO(fecha)),
    obtenerGastosDiarios,
    loading,
    refetch: fetchData,
  };
}